/**
 * Sprint 14.1b — Candidate Evaluations pill row.
 *
 * Renders a horizontally scrolling row of evaluation pills with an inline
 * "+ New Evaluation" CTA. Replaces the legacy "Interview records" pill row
 * which was disabled-with-toast scaffolding.
 *
 * Each pill shows:
 *   • evaluation name (user-given)
 *   • status dot (green=active, slate=closed)
 *   • pipeline name (compact)
 *   • "locked" badge if pipeline lock has engaged (first_activity_at set)
 *
 * The active pill (= selectedEvaluationId) is visually emphasised. Clicking
 * any pill switches selection; clicking the "+ New Evaluation" CTA opens
 * the NewEvaluationModal (managed by the parent CandidateDetail page).
 */
import React from 'react';
import { History, Plus, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const STATUS_DOT = {
  active: 'bg-emerald-500',
  closed: 'bg-slate-400',
};

function StatusDot({ status }) {
  return (
    <span
      className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${STATUS_DOT[status] || 'bg-slate-300'}`}
      aria-hidden="true"
    />
  );
}

export function EvaluationsPillRow({
  evaluations = [],
  selectedEvaluationId,
  onSelect,
  onNewEvaluation,
  loading = false,
  canCreate = true,
}) {
  const selected = evaluations.find((e) => e.id === selectedEvaluationId) || evaluations[0];

  return (
    <Card className="card-elevated" data-testid="evaluations-pill-row">
      <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2 overflow-x-auto">
          <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <History className="h-3.5 w-3.5" /> Evaluations
            {!loading && evaluations.length > 0 ? (
              <span className="font-mono normal-case tracking-normal text-foreground/60" data-testid="evaluations-count">
                ({evaluations.length})
              </span>
            ) : null}
          </span>
          <div className="flex items-center gap-1.5">
            {loading && (
              <span className="text-xs text-muted-foreground" data-testid="evaluations-loading">
                Loading evaluations…
              </span>
            )}
            {!loading && evaluations.length === 0 && (
              <span className="text-xs text-muted-foreground" data-testid="evaluations-empty">
                {canCreate ? (
                  <>No evaluations yet. Click <strong>+ New Evaluation</strong> to start one.</>
                ) : (
                  <>No evaluations yet.</>
                )}
              </span>
            )}
            {!loading &&
              evaluations.map((ev) => {
                const isActive = selected?.id === ev.id;
                return (
                  <TooltipProvider key={ev.id} delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => onSelect?.(ev.id)}
                          data-testid={`evaluation-pill-${ev.id}`}
                          data-active={isActive ? 'true' : 'false'}
                          className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                            isActive
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'bg-card text-foreground/70 hover:bg-accent'
                          }`}
                        >
                          <StatusDot status={ev.status} />
                          <span className="max-w-[180px] truncate">{ev.name}</span>
                          {ev.locked && (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                              <Lock className="h-2.5 w-2.5" /> Locked
                            </span>
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" align="start">
                        <p className="text-xs">
                          <span className="font-semibold">{ev.name}</span>
                          <span className="ml-1.5 text-primary-foreground/80">
                            ({ev.status}{ev.partner_eval_type ? ` · Type ${ev.partner_eval_type}` : ''})
                          </span>
                        </p>
                        {ev.pipeline_name && (
                          <p className="text-[11px] text-primary-foreground/80">
                            Pipeline: {ev.pipeline_name}
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selected && (
            <p className="hidden text-xs text-muted-foreground md:block">
              Viewing{' '}
              <span className="font-semibold text-foreground">{selected.name}</span>
              {selected.pipeline_name && ` · ${selected.pipeline_name}`}
              {selected.locked && ' · pipeline locked'}
            </p>
          )}
          {canCreate && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={onNewEvaluation}
              data-testid="new-evaluation-button"
            >
              <Plus className="h-3.5 w-3.5" /> New Evaluation
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
